import PropTypes from "prop-types";
import { Fragment, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { styled, alpha } from "@mui/material/styles";
import { Box, Drawer, Stack, Theme, CSSObject, useTheme } from "@mui/material";
import useResponsive from "../../../hooks/useResponsive";
import Logo from "../../../components/Logo";
import Scrollbar from "../../../components/ScrollBar";
import NavSection from "../../../components/NavSection";
import getNavConfig from "./config";
import { RootState } from "../../../store";
import Iconify from "../../../components/Iconify";
import { App } from "../../../models";
import { getSubscription } from "../../../utils";
import { isFoodBusiness } from "../../../pages/product/Costing/lib";

const NAV_WIDTH = 280;
const CLOSE_NAV_WIDTH = 70;

const StyledAccount = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2, 2.5),
  borderRadius: Number(theme.shape.borderRadius) * 1.5,
  backgroundColor: alpha(theme.palette.grey[500], 0.12),
}));

const openedMixin = (theme: Theme): CSSObject => ({
  width: NAV_WIDTH,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

Nav.propTypes = {
  open: PropTypes.bool,
  setOpen: PropTypes.func,
};

export default function Nav({ open, setOpen }: any) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const theme = useTheme();
  const appUser = useSelector((state: RootState) => state.appUser);
  const user = appUser.user;

  const configData = useMemo(
    () =>
      getNavConfig(
        t,
        process.env.REACT_APP_APP || App.APP,
        user?.role || "",
        !!user?.currentClient?.isBranch,
        user?.access || [],
        isFoodBusiness(
          process.env.REACT_APP_APP || App.APP,
          Object.keys(getSubscription(appUser) || {}),
        ),
      ),
    [t, user?.role, user?.currentClient?.isBranch, user?.access, user?.client],
  );
  // const [open, setOpen] = useState(false);

  const isDesktop = useResponsive("up", "lg");

  useEffect(() => {
    if (open && !isDesktop) {
      setOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, []);
  }, [pathname]);

  const renderContent = (
    <Fragment>
      <Box sx={{ px: 1, pt: 3, pb: 1, display: "inline-flex" }}>
        <Logo hasName={!isDesktop || open} />
      </Box>
      <Scrollbar
        sx={{
          height: 1,
          "& .simplebar-content": {
            height: 1,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {isDesktop && (
          <Iconify
            icon={
              open ? "eva:chevron-left-outline" : "eva:chevron-right-outline"
            }
            sx={{
              color: "text.secondary",
              bgColor: "action.disabledOpacity",
              borderColor: "action.disabledBackground",
              backdropFilter: "blur(6px)",
              cursor: "pointer",
              borderRadius: "50%",
              borderWidth: "1px",
              borderStyle: "dashed",
              width: 25,
              height: 25,
              position: "fixed",
              left: open ? NAV_WIDTH - 13 : CLOSE_NAV_WIDTH - 18,
              top: 25,
            }}
            onClick={() => setOpen(!open)}
          />
        )}

        {/* {appUser && appUser.user && (!isDesktop || open) && (
          <Box sx={{ mb: 3, mx: 2.5 }}>
            <Link underline="none">
              <StyledAccount>
              <Avatar
                file={
                  appUser.user?.picture?.length > 0
                  ? appUser.user?.picture
                  : account.photoURL
                }
                alt={appUser.user?.name}
                baseUrl={
                  appUser.user?.picture?.length > 0
                  ? FILE_BASE_URL.PROFILE_PICTURE
                  : ""
                }
                />
                <Box sx={{ ml: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ color: "text.primary" }}
                  >
                    {appUser.user.name}
                  </Typography>

                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {t(sentenceCase(appUser.user?.role))}
                  </Typography>
                </Box>
              </StyledAccount>
            </Link>
          </Box>
        )} */}

        <NavSection data={configData} isDesktop={isDesktop} open={open} />

        <Box sx={{ flexGrow: 1 }} />

        <Box sx={{ px: 2.5, pb: 3, mt: 10 }}>
          <Stack
            alignItems="center"
            spacing={3}
            sx={{ pt: 5, borderRadius: 2, position: "relative" }}
          >
            <Box
              component="a"
              href="https://www.ideasnewit.com/"
              target="_blank"
              title={t("DevelopedCompany").toString()}
              sx={{
                width: open ? "65%" : "100%",
                position: "absolute",
                top: -50,
              }}
            >
              <Box
                component="img"
                src={open ? "/assets/logo-init.png" : "/assets/logo-init-2.png"}
                // sx={{ width: 100, position: "absolute", top: -50 }}
              />
            </Box>

            {/*
          <Box sx={{ textAlign: "center" }}>
            <Typography gutterBottom variant="h6">
              Get more?
            </Typography>

            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              From only $69
            </Typography>
          </Box> */}

            {/* <a
            href="https://www.ideasnewit.com/"
            target="_blank"
            title={t("DevelopedCompany")}
          >
            IdeasNew InfoTech
          </a> */}
          </Stack>
        </Box>
      </Scrollbar>
    </Fragment>
  );

  return (
    <Box
      component="nav"
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: open ? NAV_WIDTH : CLOSE_NAV_WIDTH },
      }}
    >
      {isDesktop ? (
        <Drawer
          open
          variant="permanent"
          PaperProps={{
            sx: {
              width: open ? NAV_WIDTH : CLOSE_NAV_WIDTH,
              bgcolor: "background.default",
              borderRightStyle: "dashed",

              ...(open && {
                ...openedMixin(theme),
                "& .MuiDrawer-paper": openedMixin(theme),
              }),
              ...(!open && {
                ...closedMixin(theme),
                "& .MuiDrawer-paper": closedMixin(theme),
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      ) : (
        <Drawer
          open={open}
          onClose={setOpen}
          ModalProps={{
            keepMounted: true,
          }}
          PaperProps={{
            sx: { width: NAV_WIDTH },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}
